import React, { useState, useRef, useEffect, useContext } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
    Button, Dropdown, DropdownToggle,
    DropdownMenu, FormGroup, Label,
    Input, Form
} from 'reactstrap';
import axios from 'axios';
import { ReportContext } from '../context/ReportProvider';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import PinReport from '../PinReport/PinReport';
import { useSearchParams } from 'react-router-dom';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
    ICustomReportProps, IExtendedPropsInputs, IReportResponse,
    ISaveMemorizedReport, IUpdateMemorizedReport
} from './ICustomReport';
import { removeMemIdParam } from '../../../utils/removeMemIdParams';

const idSchema = yup
    .number()
    .typeError('Must be a number')
    .positive('Must be a positive number')
    .required('Required');

const validationSchema = yup.object({
    reportName: yup
        .string()
        .required('Report name is required')
        .min(3, 'Report name must be at least 3 characters'),
    memorizeOption: yup
        .mixed<'department' | 'user'>()
        .oneOf(['department', 'user'], 'Invalid option')
        .required('Memorize option is required'),
    favoriteReport: yup.boolean().required(),
    reportId: idSchema.label('Report ID'),
    holderId: idSchema.label('Holder ID'),
    userId: idSchema.label('User ID'),
    jsonFilterValue: yup
        .object()
        .required('JSON filter is required')
});

const CustomReport: React.FC<ICustomReportProps> = ({ qSightReportId, qSightHolderId, qSightUserId, currReportMemId, memReportResponse }) => {

    const reportContext = useContext(ReportContext);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [queryParams, setQueryParams] = useSearchParams();
    const [memReportId, setMemReportId] = useState<string | false>(false);
    const requestBaseUrl = process.env.REACT_APP_REPORT_API_URL;

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        clearErrors,
        reset,
        formState: { errors }
    } = useForm<IExtendedPropsInputs>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            reportName: '',
            memorizeOption: 'user',
            favoriteReport: reportContext?.isPinReport,
            reportId: qSightReportId,
            holderId: qSightHolderId,
            userId: qSightUserId,
            jsonFilterValue: {},
        },
    });

    const favoriteReportState = watch('favoriteReport');

    const toggleDropdown = () => setDropdownOpen(prev => !prev);

    const onSubmit = async (data: IExtendedPropsInputs): Promise<void> => {

        const isUpdate = !!memReportId && memReportId !== 'false';
        let requestBody: ISaveMemorizedReport | IUpdateMemorizedReport;

        try {

            if (isUpdate) {
                requestBody = {
                    reportID: data.reportId,
                    holderID: data.holderId,
                    savedName: data.reportName,
                    forUserID: data.memorizeOption === 'user' ? data.userId : null,
                };
            }
            else {
                requestBody = {
                    reportID: data.reportId,
                    holderID: data.holderId,
                    savedName: data.reportName,
                    propertyName: 'JsonFilter',
                    propertyValue: JSON.stringify(data.jsonFilterValue),
                    forUserID: data.memorizeOption === 'user' ? data.userId : null,
                };
            }

            const response = await axios.request<IReportResponse>({
                method: isUpdate ? 'put' : 'post',
                url: isUpdate
                    ? `${requestBaseUrl}updatememorizedreport?memorizedReportId=${memReportId}`
                    : `${requestBaseUrl}savememorizedreport`,
                data: requestBody,
            });

            if (response.status === 200 && response.data.success) {
                toast.success(`Congratulations! You have successfully ${isUpdate ? 'updated' : 'saved'} your custom report.`);
                reportContext?.setMemorizedListUpdates((prev: any) => !prev);
                if (isUpdate) {
                    reportContext?.setIsUpdatedMemReport((prev: any) => !prev);
                }
                if (!isUpdate) {
                    // Reset form to initial state
                    reset({
                        reportName: '',
                        memorizeOption: 'user',
                        favoriteReport: reportContext?.isPinReport,
                        reportId: qSightReportId,
                        holderId: qSightHolderId,
                        userId: qSightUserId,
                        jsonFilterValue: {},
                    });
                }
                setDropdownOpen(false);
            } else {
                toast.error(`Sorry, failed to ${isUpdate ? 'update' : 'save'} your custom report.`);
            }

        } catch (error: any) {

            const status = error?.response?.status;
            const message = error?.response?.data?.message || '';

            if (status === 409 && message.includes('already exists')) {
                toast.error('This report name is already in use. Kindly select an alternative name.');
            } else {
                console.error('Error saving/updating memorized report:', error);
                toast.error(`Sorry, failed to ${isUpdate ? 'update' : 'save'} your custom report.`);
            }

        } finally {
            //reset();
            //reportContext?.setReportFilter({});
        }
    };

    const checkJsonFilterData = (): void => {
        clearErrors();
        const jsonFilter = reportContext?.reportFilter;
        const isValidFilter = jsonFilter !== null &&
            typeof jsonFilter === 'object' && Object.keys(jsonFilter).length > 0;

        if (isValidFilter) {
            setValue('jsonFilterValue', jsonFilter);
            handleSubmit(onSubmit)();
        } else {
            toast.error('Please apply filters before saving the custom report.');
        }
    };

    const handleReportDelete = async (): Promise<void> => {

        if (!memReportId) {
            toast.error('Kindly select custom report to delete!');
            return;
        }

        try {

            const requestUrl = `${requestBaseUrl}deletememorizedreportbyid?memorizedReportId=${memReportId}`;

            const response = await axios.delete(requestUrl);

            if (response.status === 200 && response.data.success) {
                const reportName = memReportResponse?.savedName || 'Custom Report';
                toast.success(`You have deleted ${reportName} successfully.`);

                reportContext?.setMemorizedListUpdates((prev: boolean) => !prev);
                reportContext?.setReportFilter({});
                if (reportContext?.setMemorizedData) {
                    reportContext?.setMemorizedData({});
                }
                setDropdownOpen(false);

                const updatedParams = removeMemIdParam(queryParams);
                setQueryParams(updatedParams);

                setMemReportId(false);
                // Reset form to initial state
                reset({
                    reportName: '',
                    memorizeOption: 'user',
                    favoriteReport: reportContext?.isPinReport,
                    reportId: qSightReportId,
                    holderId: qSightHolderId,
                    userId: qSightUserId,
                    jsonFilterValue: {},
                });

            } else {
                toast.error('Failed to delete custom report.');
            }
        } catch (error) {
            console.error('Deleting report error:', error);
            toast.error('An error occurred while deleting the report.');
        }
    };

    useEffect(() => {

        setMemReportId(currReportMemId);

        if (currReportMemId && memReportResponse) {
            reportContext?.setReportFilter(JSON.parse(memReportResponse.propertyValue));
            setValue('reportName', memReportResponse.savedName);
            setValue('memorizeOption', memReportResponse.forUserID ? 'user' : 'department');
        }
        else {
            // Reset form to initial state with updated props
            reset({
                reportName: '',
                memorizeOption: 'user',
                favoriteReport: reportContext?.isPinReport,
                reportId: qSightReportId,
                holderId: qSightHolderId,
                userId: qSightUserId,
                jsonFilterValue: {},
            });
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);

    }, [currReportMemId, memReportResponse]);

    return (
        <div ref={dropdownRef}>
            <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                <DropdownToggle caret color="primary" outline>
                    {memReportId ? <div><i className="bi bi-pencil-square mr-2" />Edit Report</div>
                        : <div><i className="bi bi-file-earmark-plus mr-1" />Custom Report</div>}
                </DropdownToggle>
                <DropdownMenu right style={{ padding: '15px', minWidth: '280px' }}>
                    <Form>
                        <FormGroup>
                            <Label for="reportName">Name of Report</Label>
                            <Controller
                                name="reportName"
                                control={control}
                                rules={{ required: 'Report name is required' }}
                                render={({ field, fieldState: { error } }) => (
                                    <>
                                        <Input
                                            type="text"
                                            id="reportName"
                                            placeholder="Enter report name"
                                            {...field}
                                            invalid={!!error}
                                        />
                                        {errors.reportName && <div className="text-danger small mt-1">{errors.reportName.message}</div>}
                                    </>
                                )}
                            />
                        </FormGroup>
                        <FormGroup check className="mb-2">
                            <Label check>
                                <Input
                                    type="checkbox"
                                    style={{ width: '14px', height: '14px', marginTop: '6px' }}
                                    checked={watch('memorizeOption') === 'department'}
                                    onChange={() =>
                                        setValue(
                                            'memorizeOption',
                                            watch('memorizeOption') === 'department' ? 'user' : 'department'
                                        )
                                    }
                                />
                                Share with my department
                            </Label>
                        </FormGroup>
                        {errors.memorizeOption && <div className="text-danger small mt-1">{errors.memorizeOption.message}</div>}
                        <FormGroup check className="mb-2">
                            <Label
                                htmlFor="favoriteReportCheckbox"
                                style={{ cursor: 'pointer', color: '#007bff', userSelect: 'none', marginLeft: '-20px' }}
                                title={favoriteReportState ? 'Click to unfavorite' : 'Click to favorite'}
                            >
                                <Controller
                                    name="favoriteReport"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Input
                                                type="checkbox"
                                                id="favoriteReportCheckbox"
                                                name={field.name}
                                                checked={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                innerRef={field.ref}
                                                style={{ display: 'none' }}
                                            />
                                            {qSightReportId && qSightUserId && (
                                                <PinReport reportId={qSightReportId} userId={qSightUserId} />
                                            )}
                                        </>
                                    )}
                                />
                            </Label>
                        </FormGroup>
                        <div className="d-flex">
                            {memReportId && (
                                <Button color="light" type="button" onClick={() => handleReportDelete()}
                                    className="w-50 border border-dark text-dark d-flex align-items-center justify-content-center mr-2">
                                    <i className="bi bi-trash3 ml-2 mr-2" />
                                    <div className='mr-3'>Delete</div>
                                </Button>
                            )}
                            <Button color="primary" type="button" onClick={() => checkJsonFilterData()}
                                className="w-100 d-flex align-items-center justify-content-center">
                                Save Changes
                            </Button>
                        </div>
                        {(['reportId', 'holderId', 'userId', 'jsonFilterValue'] as (keyof IExtendedPropsInputs)[]).map((field) =>
                            errors[field] ? <div key={field} className="text-danger small mt-1">{errors[field]?.message}</div> : null
                        )}
                    </Form>
                </DropdownMenu>
            </Dropdown>
        </div>
    );
};

export default CustomReport;

